import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { getAuth, GoogleAuthProvider, onIdTokenChanged } from 'firebase/auth';
import { getFirestore, addDoc, collection } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import { getAnalytics, logEvent } from 'firebase/analytics';

// Prefer Vite env (import.meta.env) then CRA env (process.env)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const envAny: any = typeof import.meta !== 'undefined' ? (import.meta as any).env : undefined;
const read = (keys: string[] | string, fallback = ''): string => {
  const list = Array.isArray(keys) ? keys : [keys];
  for (const k of list) {
    const vVite = envAny?.[k];
    if (typeof vVite === 'string' && vVite.length > 0) return vVite;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const vProc = (typeof process !== 'undefined' ? (process.env as any)?.[k] : undefined) as
      | string
      | undefined;
    if (typeof vProc === 'string' && vProc.length > 0) return vProc;
  }
  return fallback;
};

const firebaseConfig = {
  apiKey: read(
    ['VITE_FIREBASE_API_KEY', 'REACT_APP_FIREBASE_API_KEY'],
    'AIzaSyBwHzhhDij2z9nqrEuWb1Cjep1ke9t1Drg'
  ),
  authDomain: read(
    ['VITE_FIREBASE_AUTH_DOMAIN', 'REACT_APP_FIREBASE_AUTH_DOMAIN'],
    'autotaxpilot.firebaseapp.com'
  ),
  projectId: read(['VITE_FIREBASE_PROJECT_ID', 'REACT_APP_FIREBASE_PROJECT_ID'], 'autotaxpilot'),
  storageBucket: read(
    ['VITE_FIREBASE_STORAGE_BUCKET', 'REACT_APP_FIREBASE_STORAGE_BUCKET'],
    'autotaxpilot.firebasestorage.app'
  ),
  messagingSenderId: read(
    ['VITE_FIREBASE_MESSAGING_SENDER_ID', 'REACT_APP_FIREBASE_MESSAGING_SENDER_ID'],
    '717202735263'
  ),
  appId: read(
    ['VITE_FIREBASE_APP_ID', 'REACT_APP_FIREBASE_APP_ID'],
    '1:717202735263:web:5bbd55973499d9c404e2aa'
  ),
  measurementId: read(
    ['VITE_FIREBASE_MEASUREMENT_ID', 'REACT_APP_FIREBASE_MEASUREMENT_ID'],
    'G-9MRP6P1N3W'
  ),
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app, 'us-central1'); // Iowa region for free tier

// Initialize Analytics (only in production)
export const analytics = typeof window !== 'undefined' ? getAnalytics(app) : null;
export function track(eventName: string, params?: Record<string, any>) {
  try {
    if (analytics) logEvent(analytics as any, eventName, params as any);
  } catch {
    // no-op
  }
}

/**
 * Log receipt upload audit data for cost telemetry and analytics
 */
export async function logReceiptUploadAudit(
  userId: string,
  expenseId: string,
  auditData: {
    originalBytes: number;
    finalBytes: number;
    mime: string;
    width: number;
    height: number;
    compressionRatio: number;
    exifSummary?: any;
    uploadDuration?: number;
    success: boolean;
    errorMessage?: string;
  }
) {
  try {
    // Fire analytics event
    track('receipt_upload_result', {
      expenseId,
      originalSizeKB: Math.round(auditData.originalBytes / 1024),
      finalSizeKB: Math.round(auditData.finalBytes / 1024),
      compressionRatio: Math.round(auditData.compressionRatio * 100) / 100,
      mime: auditData.mime,
      dimensions: `${auditData.width}x${auditData.height}`,
      success: auditData.success,
      duration: auditData.uploadDuration,
      sizeBucket:
        auditData.finalBytes < 350 * 1024
          ? '<350KB'
          : auditData.finalBytes < 700 * 1024
            ? '350-700KB'
            : '>700KB',
      longSidePx:
        Math.max(auditData.width, auditData.height) < 1200
          ? '<1200px'
          : Math.max(auditData.width, auditData.height) < 2200
            ? '1200-2200px'
            : '>2200px',
    });

    // Store minimal audit record in Firestore (no personal EXIF, only device summary)
    const auditDoc = {
      timestamp: new Date(),
      originalBytes: auditData.originalBytes,
      finalBytes: auditData.finalBytes,
      mime: auditData.mime,
      dimensions: { width: auditData.width, height: auditData.height },
      compressionRatio: auditData.compressionRatio,
      deviceSummary: auditData.exifSummary
        ? {
            make: auditData.exifSummary.make,
            model: auditData.exifSummary.model,
          }
        : null,
      success: auditData.success,
      errorMessage: auditData.errorMessage,
      uploadDuration: auditData.uploadDuration,
    };

    // Store in audit collection (matches Firestore rules path)
    await addDoc(collection(db, 'users', userId, 'audit'), auditDoc);
  } catch (error) {
    console.warn('Failed to log receipt upload audit:', error);
    // Don't throw - audit logging shouldn't break the main flow
  }
}

// Configure Google Auth Provider
export const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');

export default app;

export function observeIdToken(callback: Parameters<typeof onIdTokenChanged>[1]) {
  return onIdTokenChanged(auth, callback);
}

// App Check (reCAPTCHA v3) — optional in dev, recommended in prod
// Ensure you set REACT_APP_RECAPTCHA_V3_SITE_KEY in your environment when enabling
try {
  // Enable App Check debug token for local/dev when explicit flag is set
  if (
    typeof window !== 'undefined' &&
    envAny?.MODE !== 'production' &&
    envAny?.DEV === true &&
    (read(['VITE_APPCHECK_DEBUG', 'REACT_APP_APPCHECK_DEBUG']) === 'true' ||
      read(['VITE_APPCHECK_DEBUG', 'REACT_APP_APPCHECK_DEBUG']) === '1')
  ) {
    (window as any).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    // eslint-disable-next-line no-console
    console.log('[AppCheck] debug token enabled');
  }

  const siteKey = read(['VITE_RECAPTCHA_V3_SITE_KEY', 'REACT_APP_RECAPTCHA_V3_SITE_KEY']);
  if (siteKey) {
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });
    // eslint-disable-next-line no-console
    console.log('[AppCheck] initialized with ReCaptchaV3Provider');
  } else {
    // eslint-disable-next-line no-console
    console.log(
      '[AppCheck] site key missing; initialization skipped (auth will work without App Check)'
    );
  }
} catch (e) {
  // eslint-disable-next-line no-console
  console.warn('[AppCheck] init error (non-fatal):', e);
}
